import { canUseExperimentationOnClient } from '../../consent/waitingForConsent'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { trackEvent } from 'analytics/analytics'
import { setChertoExperiment } from 'actions/ChertoActions'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import config from 'constants/config'
import type { ExperimentConfig, ExperimentId } from 'constants/experiments'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getChertoBucketingId } from 'lib/experiments/chertoBucketingId'
import { getExperimentActivationEvent } from 'lib/experiments/experimentActivation'
import type { RouterLocation } from 'lib/routing/appRouting'
import isBot from 'lib/web/isBot'
import {
  isAllowedToTriggerExperiment,
  setTriggeredCookie,
} from 'lib/optimizely/optimizelyUtils'

const activatableExperimentSet = new Set([
  ...(config.OPTIMIZELY_EXPERIMENTS ?? []),
  ...(config.CHERTO_EXPERIMENTS ?? []),
])

// One event per experiment variation and bucketing id for the page lifetime.
// Each tile mounts its own hook, and those effects run before Redux records
// `triggered`, so a per-hook ref lets every tile emit the same activation.
const sentExperimentActivations = new Set<string>()
let sentExperimentActivationLocation: RouterLocation | undefined

function experimentActivationKey(
  provider: ExperimentConfig['provider'],
  experimentId: ExperimentId,
  variationKey: string,
  bucketingId: string | undefined,
) {
  return `${provider}:${experimentId}:${variationKey}:${bucketingId ?? ''}`
}

export function resetSentExperimentActivations() {
  sentExperimentActivations.clear()
  sentExperimentActivationLocation = undefined
}

function useExperimentActivation(
  experimentId: ExperimentId,
  canTrigger: boolean = true,
  overrideTrigger = false,
  experimentProvider: ExperimentConfig['provider'] = 'optimizely',
) {
  const dispatch = useAppDispatch()
  const hasTriggeredEvent = useRef(false)
  const previousVariationKey = useRef<string | undefined>(undefined)
  const previousBucketingId = useRef<string | undefined>(undefined)
  const location = useAppSelector(state => state.router.location)
  const previousLocation = useRef(location)
  const utmContent = useAppSelector(state => state.utm.content)
  const isPostLoginExperimentSyncing = useAppSelector(state => state.optimizely.isPostLoginExperimentSyncing)

  const canTriggerExperiment = useMemo(() => {
    if (!activatableExperimentSet.has(experimentId)) {
      return false
    }
    if (isPostLoginExperimentSyncing) {
      return false
    }
    if (overrideTrigger) {
      return canTrigger
    }
    return isAllowedToTriggerExperiment(canTrigger, utmContent, experimentId)
  }, [canTrigger, isPostLoginExperimentSyncing, overrideTrigger, utmContent, experimentId])

  const experiment = useAppSelector(state => experimentProvider === 'cherto' ?
    state.cherto.chertoExperiments[experimentId] :
    state.optimizely.optimizelyExperiments[experimentId])
  const selectBucketingId = useCallback((state: App.State) => {
    if (experimentProvider !== 'cherto') {
      return undefined
    }

    return getChertoBucketingId(state.cherto.units, experimentId)
  }, [experimentId, experimentProvider])
  const bucketingId = useAppSelector(selectBucketingId)

  const activate = useCallback(() => {
    // Apply to both automatic and imperative activation, including UTM overrides.
    if (isBot || !canUseExperimentationOnClient()) return

    // The router creates a new location for each navigation, including a return
    // to the same history entry. Clear the shared set before checking it.
    if (sentExperimentActivationLocation !== location) {
      resetSentExperimentActivations()
      sentExperimentActivationLocation = location
    }
    if (previousLocation.current !== location) {
      previousLocation.current = location
      hasTriggeredEvent.current = false
    }

    const isActivatableExperiment = activatableExperimentSet.has(experimentId) && (
      overrideTrigger || isAllowedToTriggerExperiment(true, utmContent, experimentId)
    )
    if (isPostLoginExperimentSyncing || !isActivatableExperiment || hasTriggeredEvent.current || experiment?.triggered || !experiment) {
      return
    }

    const activationKey = experimentActivationKey(experimentProvider, experimentId, experiment.variationKey, bucketingId)
    if (sentExperimentActivations.has(activationKey)) {
      hasTriggeredEvent.current = true
      return
    }

    const activationEvent = getExperimentActivationEvent(experimentId, experiment, bucketingId)
    if (activationEvent) {
      sentExperimentActivations.add(activationKey)
      trackEvent(activationEvent)

      if (experiment.provider === 'cherto') {
        dispatch(setChertoExperiment(experimentId, experiment))
      } else {
        dispatch(setOptimizelyExperiment(experimentId, experiment))
      }
      setTriggeredCookie(experimentId, experiment.provider ?? 'optimizely')
      hasTriggeredEvent.current = true
    }
  }, [bucketingId, dispatch, experiment, experimentId, experimentProvider, isPostLoginExperimentSyncing, location, overrideTrigger, utmContent])

  const currentVariationKey = experiment?.variationKey
  const experimentTriggered = experiment?.triggered
  useEffect(() => {
    const variationChanged = currentVariationKey !== previousVariationKey.current
    const bucketingChanged = bucketingId !== previousBucketingId.current
    if (variationChanged || bucketingChanged) {
      // Login rebucketing can keep the same variant while a Customer BK
      // experiment gains the member id. triggered is cleared in that case so
      // this activation sends the new bucketing id. Domain User ID experiments
      // do not get a bucketing id, so a visitor-id change does not re-fire.
      if (hasTriggeredEvent.current && experimentTriggered === false) {
        hasTriggeredEvent.current = false
      }
      previousVariationKey.current = currentVariationKey
      previousBucketingId.current = bucketingId
    }
  }, [bucketingId, currentVariationKey, experimentTriggered])

  useEffect(() => {
    if (canTriggerExperiment) {
      activate()
    }
  }, [activate, canTriggerExperiment])

  return activate
}

export default useExperimentActivation
